import { useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import { ApiError } from "../../api/errors";
import { useDeleteInstance, useInstances, useProviderCredentials } from "../../api/queries";
import type { Instance } from "../../api/types";
import { EmptyState } from "../../components/AsyncStates";
import { Button } from "../../components/Button";
import { DataTable, type DataTableColumn } from "../../components/DataTable";
import { InstanceStatusPill } from "../../components/StatePill";
import { PageHeader } from "../../components/PageHeader";
import { PaginatorControls, usePaginator } from "../../components/Paginator";
import { QueryStates } from "../../components/QueryStates";
import { useFleet } from "../../fleet/FleetContext";
import { useToast } from "../../toast/ToastContext";
import { DeleteInstanceDialog } from "./DeleteInstanceDialog";
import { ProvisionInstanceModal } from "./ProvisionInstanceModal";
import "../../components/Form.css";

export function InstancesListPage() {
  const { currentId, isLoading: fleetLoading } = useFleet();
  const { session } = useAuth();
  const { pushToast } = useToast();
  const paginator = usePaginator(currentId);
  const { data, isPending, isError, error, refetch, isFetching } = useInstances(currentId, undefined, {
    cursor: paginator.cursor,
  });
  const { data: credentialsPage } = useProviderCredentials(currentId);
  const deleteMutation = useDeleteInstance(currentId);

  const [provisionOpen, setProvisionOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Instance | null>(null);

  // Mirrors the server's authz matrix exactly (server/internal/authz/
  // model.go): instance:create is granted to operator AND admin (an
  // operator can spend against an EXISTING credential); instance:PURGE
  // (destroying a host, not just "deleting" a row) is admin-only — the same
  // boundary as stack:purge, because it destroys the root disk and every
  // volume on it. UX only (S-8); the server enforces the real decision.
  const canProvision = session?.role === "admin" || session?.role === "operator";
  const canDestroy = session?.role === "admin";
  const hasCredentials = (credentialsPage?.items.length ?? 0) > 0;

  function handleDeleteConfirm() {
    if (!deleteTarget) return;
    const { id } = deleteTarget;
    deleteMutation.mutate(
      { id },
      {
        onSuccess: () => {
          pushToast({ title: `Instance "${id}" destroyed.`, tone: "success" });
          setDeleteTarget(null);
        },
        onError: (err) => {
          pushToast({
            title: `Couldn't destroy "${id}"`,
            description: err instanceof Error ? err.message : "Try again in a moment.",
            tone: "error",
          });
        },
      },
    );
  }

  const provisionButton = (
    <Button variant="primary" onClick={() => setProvisionOpen(true)} disabled={!hasCredentials}>
      Provision instance
    </Button>
  );

  const columns: DataTableColumn<Instance>[] = [
    { key: "id", header: "ID", cell: (i) => i.id, className: "data-table__primary data-table__mono" },
    { key: "status", header: "Status", cell: (i) => <InstanceStatusPill status={i.status} /> },
    { key: "region", header: "Region", cell: (i) => i.region, className: "data-table__mono" },
    { key: "serverType", header: "Server type", cell: (i) => i.serverType, className: "data-table__mono" },
    {
      key: "publicIpv4",
      header: "Public IPv4",
      cell: (i) => i.publicIpv4 || "—",
      className: (i) => (i.publicIpv4 ? "data-table__mono" : "data-table__empty-cell"),
    },
    ...(canDestroy
      ? [
          {
            key: "actions",
            header: <span className="visually-hidden">Actions</span>,
            cell: (i: Instance) => (
              <Button variant="danger" disabled={i.status === "deleting"} onClick={() => setDeleteTarget(i)}>
                Destroy
              </Button>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <PageHeader
        title="Instances"
        description="Compute Comebye provisions on your behalf via a provider credential — the Hetzner half of the hero flow."
        actions={
          canProvision ? (
            provisionButton
          ) : (
            <span className="page-header__hint">Ask an operator or admin to provision instances.</span>
          )
        }
      />

      {canProvision && !hasCredentials && (
        <p className="form-note">Add a provider credential first — there's nothing to provision on yet.</p>
      )}

      <QueryStates
        isLoading={fleetLoading || isPending}
        loadingLabel="Loading instances…"
        isError={isError}
        error={error}
        onRetry={refetch}
        errorDescription={
          error instanceof Error ? error.message : "Couldn't load instances. Check your connection and try again."
        }
        renderError={(err) =>
          err instanceof ApiError && err.code === "not_implemented" ? (
            <EmptyState
              title="Cloud provisioning isn't enabled on this control plane"
              description="An administrator can turn it on server-side (COMEBYE_PROVISIONING=1). Nothing to configure here until then."
            />
          ) : undefined
        }
        isEmpty={Boolean(data && data.items.length === 0)}
        empty={
          <EmptyState
            title="No instances provisioned yet"
            description="Provision one to give your services somewhere to run."
            action={canProvision && hasCredentials ? provisionButton : undefined}
          />
        }
      >
        {data && (
          <>
            <DataTable columns={columns} rows={data.items} rowKey={(i) => i.id} ariaBusy={isFetching} />
            <PaginatorControls
              itemCount={data.items.length}
              nextCursor={data.nextCursor}
              paginator={paginator}
              noun="instances"
            />
          </>
        )}
      </QueryStates>

      {canProvision && (
        <ProvisionInstanceModal open={provisionOpen} fleetId={currentId} onClose={() => setProvisionOpen(false)} />
      )}

      {canDestroy && (
        <DeleteInstanceDialog
          instance={deleteTarget}
          isDeleting={deleteMutation.isPending}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </>
  );
}
